import React, { useState, useEffect, Fragment, useRef } from "react";
import { useHistory } from "react-router-dom";
import MaterialTable from "material-table";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import validator from 'validator';
import { useTheme } from '@mui/material/styles';
import CloseIcon from "@material-ui/icons/Close";
import DeleteIcon from '@material-ui/icons/Delete';
import { Backdrop, Box, Button, Checkbox, CircularProgress, createTheme, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, FormControlLabel, FormHelperText, FormLabel, IconButton, InputLabel, Menu, MenuItem, Paper, Radio, RadioGroup, Select, Switch, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tabs, TextField, Typography, useMediaQuery } from "@material-ui/core";
import { NotificationManager } from "react-notifications";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { exportMaterialTableToExcel, exportMaterialTableToPDF, getDateString } from "../../utilities/utility";
import { db, getCheckSession } from "../../apis/auth";

const useStyles = makeStyles((theme) =>
    createStyles({
        button: {
            margin: theme.spacing(1),
        },
        table: {
            minWidth: 650,
        },
        root: {
            width: "100%",
        },
        instructions: {
            marginTop: theme.spacing(1),
            marginBottom: theme.spacing(1),
        },
        formControl: {
            margin: theme.spacing(1),
            minWidth: 120,
            marginRight: 20,
        },
        closeButton: {
            position: "absolute",
            right: theme.spacing(2),
            top: theme.spacing(2),
            color: theme.palette.grey[500],
        },
        selectEmpty: {
            marginTop: theme.spacing(2),
        },
        tab: {
            '& .MuiTabPanel-root': {
                padding: '0px',
            },
        },
        backdrop: {
            zIndex: theme.zIndex.drawer + 1,
            opacity: 0.5,
        },
    })
);

const ComListScreen = () => {
    const classes = useStyles();
    const history = useHistory();
    const [comList, setComList] = useState([]);
    const [comData, setComData] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [name, setName] = useState('');
    const [mobile, setMobile] = useState('');
    const [nameError, setNameError] = useState('');
    const [mobileError, setMobileError] = useState('');
    const [openDialog, setOpenDialog] = useState(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(null);

    useEffect(() => {
        console.log("========== ComListScreen : OnInit() ==========");
        const checkSession = async () => {
            const sessionStatus = await getCheckSession();
            if (sessionStatus === false) {
                history.push("/");
                return;
            }
            getAllData();
        };
        checkSession();
    }, []);

    const getAllData = async () => {
        try {
            const docRef = doc(db(), 'slc_config', 'com_list');
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = docSnap.data().com_list || [];
                setComList(data.map(item => ({ ...item })));
                setComData(data.map(item => ({ ...item })));
            } else {
                setComList([]);
                setComData([]);
            }
        } catch (error) {
            console.error("Error fetching com_list:", error);
            NotificationManager.error("Unable to fetch data", "", 3000);
        }
    };

    const hideAddUserDialog = () => {
        setNameError('');
        setMobileError('');
        setOpenDialog(null);
        setSelectedRow(null);
        setName('');
        setMobile('');
    };

    const hideDeleteDialog = () => {
        setOpenDeleteDialog(null);
        setSelectedRow(null);
    };

    const update = async () => {
        let error = false;

        if (name.trim() === "" || name.trim().length <= 0) {
            setNameError("Enter Name");
            error = true;
        } else {
            setNameError("");
        }

        if (!validator.isMobilePhone(mobile, 'any')) {
            setMobileError("Enter a valid mobile number");
            error = true;
        } else {
            const mobileExists = comData.some((e) => e.mobile === mobile && e.mobile !== selectedRow?.mobile);
            if (mobileExists) {
                setMobileError("Mobile number already exists");
                error = true;
            } else {
                setMobileError("");
            }
        }

        if (error) {
            return;
        }

        try {
            const updatedData = comData.map(item =>
                item.mobile === selectedRow.mobile ? { ...item, name, mobile } : item
            );
            await updateComListData(updatedData);
            getAllData();
            hideAddUserDialog();
            NotificationManager.success("Record updated successfully", "", 3000);
        } catch (error) {
            console.error("Error updating record:", error);
            NotificationManager.error("Unable to update record", "", 3000);
        }
    };

    const add = async () => {
        let error = false;

        if (name.trim() === "" || name.trim().length <= 0) {
            setNameError("Enter Name");
            error = true;
        } else {
            setNameError("");
        }

        if (!validator.isMobilePhone(mobile, 'any')) {
            setMobileError("Enter a valid mobile number");
            error = true;
        } else {
            const mobileExists = comData.some((e) => e.mobile === mobile);
            if (mobileExists) {
                setMobileError("Mobile number already exists");
                error = true;
            } else {
                setMobileError("");
            }
        }

        if (error) {
            return;
        }

        try {
            const newData = [...comData, { name, mobile }];
            await updateComListData(newData);
            getAllData();
            hideAddUserDialog();
            NotificationManager.success("Record created successfully", "", 3000);
        } catch (error) {
            console.error("Error creating record:", error);
            NotificationManager.error("Unable to create record", "", 3000);
        }
    };

    const deleteRecord = async () => {
        try {
            const updatedData = comData.filter(item => item.mobile !== selectedRow.mobile);
            await updateComListData(updatedData);
            getAllData();
            hideDeleteDialog();
            NotificationManager.success("Record deleted successfully", "", 3000);
        } catch (error) {
            console.error("Error deleting record:", error);
            NotificationManager.error("Unable to delete record", "", 3000);
        }
    };

    const updateComListData = async (comListData) => {
        if (!comListData || !Array.isArray(comListData)) {
            return Promise.reject('Invalid input: comListData must be an array');
        }
        try {
            const docRef = doc(db(), 'slc_config', 'com_list');
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                await updateDoc(docRef, { com_list: comListData });
            } else {
                await setDoc(docRef, { com_list: comListData });
            }

            return Promise.resolve();
        } catch (error) {
            console.error(`====> Error updating com_list data: ${error}`);
            return Promise.reject(error);
        }
    };

    const getOpReportColumnList = () => {
        return [
            {
                title: 'Name',
                field: 'name',
                searchable: true,
                render: ({ name = "" }) => name,
                exportTransformer: ({ name = "" }) => name
            },
            {
                title: 'Mobile',
                field: 'mobile',
                render: ({ mobile = "" }) => mobile,
                exportTransformer: ({ mobile = "" }) => mobile
            },
        ];
    };

    return (
        <div className="reports">
            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                boxSizing: "border-box",
            }}>
                <h2>Com List</h2>
                <Button
                    style={{
                        backgroundColor: "#0071CE",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                    }}
                    onClick={() => {
                        setOpenDialog(true);
                        setName('');
                        setMobile('');
                    }}
                >
                    Add Record
                </Button>
            </div>
            <Box sx={{ width: '100%', typography: 'body1' }}>
                <div className="dashboard-table">
                    <MaterialTable
                        style={{
                            margin: "20px 0",
                            width: "100%",
                        }}
                        className={classes.table}
                        title=""


                        columns={getOpReportColumnList()}
                        data={comList}
                        actions={[
                            {
                                icon: 'edit',
                                iconProps: { color: "action" },
                                tooltip: 'Edit Record',
                                onClick: (event, rowData) => {
                                    setSelectedRow(rowData);
                                    setName(rowData.name);
                                    setMobile(rowData.mobile);
                                    setOpenDialog(true);
                                }
                            },
                            {
                                icon: () => <DeleteIcon />,
                                iconProps: { color: "action" },
                                tooltip: 'Delete Record',
                                onClick: (event, rowData) => {
                                    setSelectedRow(rowData);
                                    setOpenDeleteDialog(true);
                                }
                            },
                        ]}
                        options={{
                            exportButton: true,
                            exportAllData: true,
                            exportCsv: (columns, csvData) => { exportMaterialTableToExcel(columns, csvData, `Company report-${getDateString()}`); },
                            exportPdf: (columns, csvData) => { exportMaterialTableToPDF(columns, csvData, `Company Report-${getDateString()}`, 8, 18); },
                            search: true,
                            selection: false,
                            sorting: true,
                            pageSize: 10,
                            headerStyle: {
                                backgroundColor: "#0071CE",
                                color: "#FFF",
                                fontSize: "0.9em",
                                textAlign: "center",
                            },
                            actionsColumnIndex: -1,
                            emptyRowsWhenPaging: false,
                        }}
                        localization={{
                            toolbar: {
                                searchPlaceholder: "Search",
                                exportCSVName: "Export as Excel",
                                exportPDFName: "Export as PDF",
                            },
                        }}
                    />
                </div>
            </Box>

            <Dialog
                className="history-dialog"
                open={openDialog}
                onClose={hideAddUserDialog}
                aria-labelledby="form-dialog-title"
                sx={{
                    '& .MuiDialogContainer-root': {
                        maxWidth: '70%',
                        height: '90vh',
                    },
                }}
            >
                <DialogTitle id="editTime-dialog-title" onClose={hideAddUserDialog}>
                    {openDialog && !selectedRow ? 'New' : 'Update'} Record
                    <IconButton
                        aria-label="close"
                        className={classes.closeButton}
                        onClick={hideAddUserDialog}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <FormControl className="dialog-form" style={{ width: '100%', margin: '0px 0' }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="name"
                            label="Name"
                            error={nameError.length > 0}
                            helperText={nameError}
                            type="text"
                            value={name}
                            onChange={(e) => { setName(e.target.value); setNameError(''); }}
                        />
                    </FormControl>
                    <FormControl className="dialog-form" style={{ width: '100%', margin: '0px 0' }}>
                        <TextField
                            margin="dense"
                            id="mobile"
                            label="Mobile Number"
                            error={mobileError.length > 0}
                            helperText={mobileError}
                            type="tel"
                            value={mobile}
                            onChange={(e) => { setMobile(e.target.value); setMobileError(''); }}
                        />
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={hideAddUserDialog}>
                        Cancel
                    </Button>
                    <Button onClick={() => { openDialog && !selectedRow ? add() : update(); }} type="submit">
                        {openDialog && !selectedRow ? "Create" : "Update"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openDeleteDialog}
                onClose={hideDeleteDialog}
                aria-labelledby="delete-dialog-title"
            >
                <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the record for {selectedRow?.name} ({selectedRow?.mobile})?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={hideDeleteDialog}>Cancel</Button>
                    <Button onClick={deleteRecord} color="secondary">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default ComListScreen;